// src/features/scheduleGenerator/components/ScheduleGeneratorPreviewModal.tsx
// @deprecated Day Draft V3: 由 TaskAssignmentModal + SkeletonModal 取代。確認 QA 通過後刪除。
import React, { useState, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { format } from 'date-fns';
import { TimeBox } from '../../../types/timeBox';
import { Event } from '../../../types/calendar-event';
import { ScheduleItem, isTimeBox, isEvent } from '../../../types/scheduleItem';
import TypewriterText from '../../../components/common/TypewriterText';
import { useToast } from '../../../contexts/ToastContext';
import { TimelineTimeBoxCard, TimelineEventCard, getValidDate } from './ScheduleItemCard';
import { timeBoxCategoryConfig } from '../../../constants/timebox.constants';
import { ChevronRight, SendHorizontal } from 'lucide-react';
import { toDateObject } from '../../../utils/dateUtils';

// 計畫預覽的類型定義
export interface ScheduleGeneratorPreviewProps {
    isOpen: boolean;
    onClose: () => void;
    plan: {
        date: string;
        timeBoxes: TimeBox[];
        events?: Event[];
        scheduleItems?: ScheduleItem[];
        summary?: string;
        pastScheduleItems?: TimeBox[]; // startTime < now 的既有項目（只顯示，不參與 Apply）
    } | null;
    isLoading: boolean;
    onApply: (scheduleItems: ScheduleItem[]) => void;
    onRegenerate: (adjustmentText?: string) => void;
    /** Lesson 2 進行中時顯示引導提示 */
    isLesson2Active?: boolean;
}

// 主要 ScheduleGeneratorPreviewModal 組件
const ScheduleGeneratorPreviewModal: React.FC<ScheduleGeneratorPreviewProps> = ({
    isOpen,
    onClose,
    plan,
    isLoading,
    onApply,
    onRegenerate,
    isLesson2Active = false,
}) => {
    // Toast 通知系統
    const { showWarning } = useToast();

    // 從 plan 提取初始資料（events 唯讀）
    const { timeBoxes, events } = useMemo(() => {
        const items = plan?.scheduleItems || plan?.timeBoxes || [];
        const result = { timeBoxes: [] as TimeBox[], events: [] as Event[] };
        for (const item of items) {
            if (isTimeBox(item)) result.timeBoxes.push(item as TimeBox);
            else if (isEvent(item)) result.events.push(item as Event);
        }
        for (const e of plan?.events || []) {
            if (e.timeType === 'allday') result.events.push(e);
        }
        return result;
    }, [plan?.scheduleItems, plan?.timeBoxes, plan?.events]);

    // Draft Overview 收摺狀態
    const [isSummaryExpanded, setIsSummaryExpanded] = useState(true);
    // 記錄 summary 是否已完成打字，避免收合再展開時重跑動畫
    const summaryTypedRef = useRef(false);
    const lastSummaryRef = useRef<string | undefined>(undefined);
    if (plan?.summary !== lastSummaryRef.current) {
        lastSummaryRef.current = plan?.summary;
        summaryTypedRef.current = false;
    }

    // 過去行程預設收合，讓新計畫優先顯示
    const [isPastExpanded, setIsPastExpanded] = useState(false);

    const now = new Date();
    const pastItems = plan?.pastScheduleItems || [];

    // 調整文字輸入狀態
    const [adjustmentText, setAdjustmentText] = useState('');
    const [isAdjustmentFocused, setIsAdjustmentFocused] = useState(false);
    const [editedTimeBoxes, setEditedTimeBoxes] = useState<TimeBox[]>(timeBoxes);

    // sortedItems 依賴 editedTimeBoxes，確保刪除後畫面即時反映
    const sortedItems = useMemo(() => {
        type Tagged = { item: ScheduleItem; isAllDay: boolean; time: Date | null };
        const tagged: Tagged[] = [...editedTimeBoxes, ...events].map(item => {
            const isAllDay = isEvent(item) && (item as Event).timeType === 'allday';
            return {
                item,
                isAllDay,
                time: isAllDay ? null : getValidDate(item.startTime, plan?.date),
            };
        });
        tagged.sort((a, b) => {
            if (a.isAllDay !== b.isAllDay) return a.isAllDay ? -1 : 1;
            if (!a.time || !b.time) return 0;
            return a.time.getTime() - b.time.getTime();
        });
        return tagged.map(t => t.item);
    }, [editedTimeBoxes, events, plan?.date]);
    // 行內展開的卡片索引
    const [selectedBlockIndex, setSelectedBlockIndex] = useState<number | null>(null);

    const handleBlockSelect = (index: number) => {
        setSelectedBlockIndex(prev => prev === index ? null : index);
    };

    // 當 timeBoxes 改變時，更新 editedTimeBoxes
    React.useEffect(() => {
        // 為每個時間箱添加唯一識別標記，即使是臨時的
        const boxesWithIds = timeBoxes.map((box, idx) => ({
            ...box,
            id: box.id || `temp-${idx}`
        }));
        setEditedTimeBoxes(boxesWithIds);
    }, [timeBoxes]);

    const handleDirectDelete = (index: number) => {
        if (index < 0 || index >= editedTimeBoxes.length) return;
        const newBoxes = editedTimeBoxes.filter((_, i) => i !== index);
        setEditedTimeBoxes(newBoxes);
        setSelectedBlockIndex(null);
    };

    // 處理帶調整的重新生成
    const handleRegenerateWithAdjustment = () => {
        if (!adjustmentText.trim()) return;

        // 調用重新生成，傳入調整文字
        onRegenerate(adjustmentText.trim());

        // 清空輸入框
        setAdjustmentText('');
    };

    // 應用計畫
    const handleApply = () => {
        // 結合編輯過的 TimeBox 和原始的 Event 數據
        const scheduleItems: ScheduleItem[] = [
            ...editedTimeBoxes,
            ...events // Event 保持只讀，直接使用原始數據
        ];

        // 在應用前進行基本驗證
        if (scheduleItems.length === 0) {
            showWarning('No Items to Apply', 'Please generate a plan first before applying');
            return;
        }

        onApply(scheduleItems);
    };

    if (!isOpen) return null;

    // 格式化日期顯示
    const formattedDate = plan?.date ? format(new Date(plan.date), 'EEEE, MMMM d, yyyy') : '';

    return createPortal(
        <div className="fixed inset-0 z-modal-nested overflow-y-auto bg-neutral-900 bg-opacity-50 flex justify-center items-end sm:items-center">
            <div className="bg-white rounded-t-xl md:rounded-lg w-full sm:max-w-2xl sm:mx-auto shadow-xl transform transition-all animate-slideInUp h-modal-responsive flex flex-col overflow-hidden">
                {/* 拖動指示器 - 只在移動設備上顯示 */}
                <div className="sm:hidden w-full flex justify-center pt-3 pb-1">
                    <div className="w-10 h-1 bg-neutral-300 rounded-full"></div>
                </div>
                {/* 標題和關閉按鈕 - 統一設計語言 */}
                <div className="flex justify-between items-center p-4">
                    <div>
                        <h2 className="text-lg font-semibold text-neutral-800">Day Draft</h2>
                        <p className="text-sm text-neutral-600 font-medium">{formattedDate}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-neutral-500 hover:text-neutral-700"
                        aria-label="Close"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* 摘要 - 可收摺，統一輕量化設計 */}
                {plan?.summary && (
                    <div className="px-4 pt-2 pb-4">
                        {!isSummaryExpanded ? (
                            <button
                                onClick={() => setIsSummaryExpanded(true)}
                                aria-label="Expand overview"
                                className="w-full flex items-center justify-between p-2 hover:bg-neutral-50 rounded-md transition-colors text-left"
                            >
                                <span className="text-sm font-medium text-neutral-600">Overview</span>
                                <svg className="w-4 h-4 text-neutral-400 transform transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        ) : (
                            <div className="space-y-3">
                                <div className="w-full flex items-center justify-between p-2 hover:bg-neutral-50 rounded-md transition-colors">
                                    <span className="text-sm font-medium text-neutral-600">Overview</span>
                                    <button
                                        onClick={() => setIsSummaryExpanded(false)}
                                        aria-label="Collapse overview"
                                        className="text-neutral-400 hover:text-neutral-600 transition-all duration-200"
                                    >
                                        <svg className="w-4 h-4 transform rotate-90 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                </div>
                                <div className="p-3 bg-neutral-50 rounded-lg border border-neutral-200">
                                    <TypewriterText
                                        text={plan?.summary}
                                        className="text-sm text-neutral-700 leading-relaxed"
                                        speed={20}
                                        delay={200}
                                        skipAnimation={summaryTypedRef.current}
                                        onComplete={() => { summaryTypedRef.current = true; }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}


                {/* 時間箱列表 - Timeline 風格，完整閱讀流 */}
                <div className="p-4 flex-1 overflow-y-auto min-h-0">
                    {isLoading ? (
                        // 精緻的載入狀態設計
                        <div className="flex flex-col items-center justify-center py-16 text-center">
                            <div className="relative">
                                <div className="animate-spin rounded-full h-12 w-12 border-4 border-sky-200 border-t-sky-500"></div>
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="w-2 h-2 bg-sky-500 rounded-full animate-pulse"></div>
                                </div>
                            </div>
                            <div className="mt-4 space-y-2">
                                <p className="text-sm font-medium text-neutral-700">Generating your draft...</p>
                                <p className="text-xs text-neutral-500">This may take a few moments</p>
                            </div>
                        </div>
                    ) : editedTimeBoxes.length > 0 || pastItems.length > 0 ? (
                        <div className="relative">
                            {/* 時間軸主線 - 移動版空間優化 */}
                            <div className="absolute left-4 sm:left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-sky-500 to-sky-500"></div>

                            {/* 排程項目卡片 */}
                            <div className="space-y-4">
                                {/* 過去行程 - 以 Timeline 同樣語言呈現，但淡化且唯讀 */}
                                {pastItems.length > 0 && (
                                    <>
                                        <div className="ml-10 sm:ml-12">
                                            <button
                                                onClick={() => setIsPastExpanded(prev => !prev)}
                                                className="flex items-center gap-1.5 text-left group"
                                                aria-expanded={isPastExpanded}
                                                aria-controls="past-schedule-items"
                                            >
                                                <span className="text-xs font-semibold text-neutral-400 tracking-wider uppercase group-hover:text-neutral-500 transition-colors">
                                                    Before
                                                </span>
                                                <span className="text-xs text-neutral-300 font-normal">
                                                    {pastItems.length} {pastItems.length === 1 ? 'block' : 'blocks'}
                                                </span>
                                                <ChevronRight className={`w-3 h-3 text-neutral-300 transition-transform duration-200 ${isPastExpanded ? 'rotate-90' : ''}`} />
                                            </button>
                                        </div>
                                        {isPastExpanded && (
                                            <div id="past-schedule-items" className="opacity-40 space-y-4">
                                            {pastItems.map((item, index) => {
                                                const start = toDateObject(item.startTime);
                                                const end = toDateObject(item.endTime);
                                                const isInProgress = end > now;
                                                const categoryConfig = timeBoxCategoryConfig[item.category];
                                                return (
                                                    <div key={item.id || `past-${index}`} className="relative">
                                                        {/* 時間軸節點 */}
                                                        <div className="absolute left-2 sm:left-4 top-6 z-10">
                                                            <div className={`w-4 h-4 rounded-full bg-white border-2 flex items-center justify-center ${isInProgress ? 'border-amber-400' : 'border-neutral-300'}`}>
                                                                <div className={`w-2 h-2 rounded-full ${isInProgress ? 'bg-amber-400' : 'bg-neutral-400'}`}></div>
                                                            </div>
                                                        </div>
                                                        {/* 卡片 - 唯讀，無 hover 操作 */}
                                                        <div className="ml-10 sm:ml-12">
                                                            <div className="sm:p-4 p-3 rounded-xl bg-white border border-neutral-200 shadow-sm">
                                                                <h4 className="font-medium text-neutral-700 mb-1">{item.title}</h4>
                                                                <div className="flex items-center text-sm text-neutral-500">
                                                                    <span>{format(start, 'HH:mm')} - {format(end, 'HH:mm')}</span>
                                                                    {item.category && (
                                                                        <>
                                                                            <span className="mx-2">·</span>
                                                                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${categoryConfig?.color || 'bg-slate-100 text-slate-900'}`}>
                                                                                {categoryConfig?.label || item.category}
                                                                            </span>
                                                                        </>
                                                                    )}
                                                                    {isInProgress && (
                                                                        <>
                                                                            <span className="mx-2">·</span>
                                                                            <span className="text-xs text-amber-600 font-medium">in progress</span>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            </div>
                                        )}
                                        {editedTimeBoxes.length > 0 && (
                                            <div className="py-1 ml-10 sm:ml-12">
                                                <span className="text-xs font-semibold text-neutral-400 tracking-wider uppercase">
                                                    From here
                                                </span>
                                            </div>
                                        )}
                                    </>
                                )}

                                {(() => {
                                    let timeBoxCounter = 0;
                                    return sortedItems.map((item, index) => {
                                        if (isTimeBox(item)) {
                                            const tbIndex = timeBoxCounter++;
                                            return (
                                                <TimelineTimeBoxCard
                                                    key={item.id || `timebox-${index}`}
                                                    timeBox={item}
                                                    timeBoxIndex={tbIndex}
                                                    onDelete={handleDirectDelete}
                                                    onSelect={handleBlockSelect}
                                                    isSelected={selectedBlockIndex === tbIndex}
                                                    onAICommand={(cmd) => { onRegenerate(cmd); setSelectedBlockIndex(null); }}
                                                />
                                            );
                                        }
                                        return (
                                            <TimelineEventCard
                                                key={item.id || `event-${index}`}
                                                event={item}
                                                index={index}
                                                baseDate={plan?.date}
                                            />
                                        );
                                    });
                                })()}
                            </div>
                        </div>
                    ) : (
                        <div className="text-center p-12 text-neutral-500">
                            <svg className="w-16 h-16 text-neutral-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6l4 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-base font-medium">Couldn't fit any blocks</p>
                            <p className="text-sm text-neutral-400 mt-1">Try regenerating for a new draft</p>
                        </div>
                    )}
                </div>

                {/* 底部操作區 - 統一設計語言 */}
                <div className="border-t border-neutral-200 flex-shrink-0">
                    {/* Fine-tune 操作區域 - Floating Label 設計 */}
                    {!isLoading && editedTimeBoxes.length > 0 && (
                        <div className="px-4 pt-3 pb-3 border-b border-neutral-100 animate-fadeIn">
                            <div className="relative">
                                {/* Floating Label - 修正背景和定位 */}
                                <label
                                    className={`absolute left-3 transition-all duration-200 pointer-events-none ${
                                        isAdjustmentFocused || adjustmentText.trim()
                                            ? 'top-0 text-xs text-sky-600 transform -translate-y-1/2 bg-white px-1'
                                            : 'top-1/2 text-sm text-neutral-500 transform -translate-y-1/2'
                                    }`}
                                >
                                    Want to change the direction?
                                </label>

                                <input
                                    type="text"
                                    className={`w-full px-3 text-sm border rounded-lg focus:outline-none transition-all duration-200 pr-10 bg-white ${
                                        isAdjustmentFocused || adjustmentText.trim()
                                            ? 'pt-4 pb-2.5 leading-normal border-sky-500 focus:ring-2 focus:ring-sky-500/20'
                                            : 'py-3 border-neutral-200 focus:border-sky-500'
                                    }`}
                                    placeholder={isAdjustmentFocused ? 'e.g. "Add task X", "Keep afternoon free"' : ''}
                                    value={adjustmentText}
                                    onFocus={() => setIsAdjustmentFocused(true)}
                                    onBlur={() => setIsAdjustmentFocused(false)}
                                    onChange={(e) => setAdjustmentText(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && adjustmentText.trim()) {
                                            handleRegenerateWithAdjustment();
                                        }
                                    }}
                                />

                                <button
                                    onClick={handleRegenerateWithAdjustment}
                                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1.5 rounded-md transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed text-sky-500 hover:text-sky-600 hover:bg-sky-50"
                                    disabled={isLoading || !adjustmentText.trim()}
                                    aria-label="Regenerate with adjustments"
                                >
                                    <SendHorizontal className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* 主要操作按鈕區域 */}
                    <div className="p-4 pb-safe flex justify-end gap-3">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-white text-neutral-600 border border-neutral-300 rounded-md text-sm font-medium hover:bg-neutral-50 hover:border-neutral-400 transition-colors"
                            disabled={isLoading}
                        >
                            Cancel
                        </button>

                        <div className="relative">
                            {isLesson2Active && !isLoading && (
                                <div
                                    id="lesson2-apply-tooltip"
                                    role="tooltip"
                                    className="absolute bottom-full right-0 mb-2 w-56 bg-sky-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg z-tooltip"
                                >
                                    <p className="leading-snug">Tap Apply to add it to your calendar</p>
                                    <div className="absolute top-full right-4 w-2 h-2 bg-sky-900 rotate-45 -mt-1" aria-hidden="true" />
                                </div>
                            )}
                            <button
                                onClick={handleApply}
                                className="px-6 py-2 bg-sky-500 hover:bg-sky-600 text-white rounded-md text-sm font-semibold transition-colors flex items-center shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={isLoading || editedTimeBoxes.length === 0}
                                aria-describedby={isLesson2Active && !isLoading ? 'lesson2-apply-tooltip' : undefined}
                            >
                                {isLoading ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 14 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Applying...
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Apply
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

            </div>

        </div>,
        document.body
    );
};

export default ScheduleGeneratorPreviewModal;
